import React, { useContext, useEffect, useState } from "react";
import { MCard } from "components/Shared";
import FormEnumerationEditor from "./FormEnumerationEditor";
import { EnumerationItem } from "interfaces/Phoenix.FormsDesigner.Models";
import { TextField } from "react-md";
import { deepCopy } from "./helpers";
import IconButton from "components/Shared/IconButton";
import { showExpressionEditor } from "./CodeEditor/ExpressionEditor";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";

interface Props {
  controlEnumerationItems: EnumerationItem[];
  onChange: (newItemSet: EnumerationItem[]) => void;
};

interface ItemSet {
  condition: string;
  items: { text: string, value: string}[];
};

const ItemSetEditor = ({ controlEnumerationItems, onChange }: Props) => {
  const [itemSets, setItemSets] = useState<ItemSet[]>([]);
  
  useEffect(() => {
    setItemSets(controlEnumerationItems.map(i => {
      let splitName = i.name ? i.name.split("|") : [];
      let splitVal = i.value ? i.value.split("|") : [];

      return {
        condition: i.xpath,
        items: splitName.map((sn, i) => ({ text: sn, value: splitVal[i] })),
      };
    }));
  }, [controlEnumerationItems]);

  const onItemSetChanged = (itemSet: ItemSet[]) => {
    onChange(itemSet.map(is => ({
      name: is.items.map(i => i.text).join("|"),
      value: is.items.map(i => i.value).join("|"),
      xpath: is.condition,
    })) as EnumerationItem[]);
  };

  const onAddItemSet = () => {
    let newItemSet = deepCopy(itemSets);
    
    newItemSet.push({
      condition: "",
      items: [],
    });

    onItemSetChanged(newItemSet);
  };

  const onConditionChanged = (index: number, condition: string) => {
    let newItemSet = deepCopy(itemSets);
    newItemSet[index].condition = condition;
    onItemSetChanged(newItemSet);
  };

  const onListChanged = (index: number, items: ItemSet["items"]) => {
    let newItemSet = deepCopy(itemSets);
    newItemSet[index].items = items;
    onItemSetChanged(newItemSet);
  };

  const onDelete = (index: number) => {
    let newItemSet = deepCopy(itemSets);
    newItemSet.splice(index, 1);
    onItemSetChanged(newItemSet);
  };

  return (
    <MCard
      title="Item Sets"
      actions={[{ icon: "add", onClick: onAddItemSet }]}
      cardContentClassName="item-set-editor"
    >
      {itemSets.map((is, i) =>
        <ItemSetControl
          itemSet={is}
          onConditionChanged={condi => onConditionChanged(i, condi)}
          onListChange={list => onListChanged(i, list)}
          onDelete={() => onDelete(i)}
        />
      )}
    </MCard>
  );
};

interface ItemSetProps {
  onConditionChanged: (newCondition: string) => void;
  onListChange: (items: ItemSet["items"]) => void;
  onDelete: () => void;
  itemSet: ItemSet;
};


const ItemSetControl = ({ itemSet, onConditionChanged, onListChange, onDelete }: ItemSetProps) => {
  const dialog = useContext<DialogProviderValue>(DialogContext);

  const showExpressionEditorLocal = () =>
    showExpressionEditor(dialog, "Editing Itemset Condition", itemSet.condition, newExp => onConditionChanged(newExp));

  return <div className="item-set">
    <div className="text-field-delete">
      <TextField
        label="Condition"
        onChange={onConditionChanged}
        value={itemSet.condition}
      />
      <IconButton icon="code" onClick={showExpressionEditorLocal} />
      <IconButton icon="delete" onClick={onDelete} />
    </div>
    <FormEnumerationEditor
      items={itemSet.items}
      displayItem={item => item.text + (item.value ? ` (${item.value})` : "")}
      fields={[{ label: "Text", property: "text" }, { label: "Value", property: "value" }]}
      onChange={onListChange}
      noCard
      bulkEditFields={["text", "value"]}
    />
  </div>;
};

export default ItemSetEditor;
